// REST API クライアント。
// fetch を直接呼び出し、認証時は Authorization ヘッダを付与する。
// JSON 不一致など本来発生しないケースは throw して TanStack Query 側で扱う。

import type { CanvasObject } from "@refinement-canvas/domain";
import { getWebConfig } from "../env";

interface BootstrapResponse {
  canvasId: string;
  serverSeq: number;
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
  };
  objects: readonly CanvasObject[];
}

export interface RecentCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  lastOpenedAt: string;
}

export interface OgFetchResult {
  url: string;
  title?: string | undefined;
  description?: string | undefined;
  image?: string | undefined;
  siteName?: string | undefined;
}

export interface RealtimeSessionTicket {
  token: string;
  expiresAt: string;
}

interface PresignedUpload {
  uploadUrl: string;
  bucket: string;
  key: string;
  expiresInSeconds: number;
}

function buildHeaders(extra?: HeadersInit): HeadersInit {
  // 認証必須環境ではブラウザの認証クッキー / Authorization が
  // @meiden-connect/auth-frontend-sdk により自動付与される想定。
  // ここでは content-type だけ設定する。
  const headers = new Headers(extra);
  if (!headers.has("content-type")) headers.set("content-type", "application/json");
  return headers;
}

async function callJson<T>(path: string, init?: RequestInit): Promise<T> {
  const config = getWebConfig();
  const url = `${config.apiBaseUrl.replace(/\/$/, "")}${path}`;
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: buildHeaders(init?.headers),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${body || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ---- canvas ----

export function fetchCanvasBootstrap(canvasId: string): Promise<BootstrapResponse> {
  return callJson<BootstrapResponse>(`/canvases/${encodeURIComponent(canvasId)}`);
}

export function fetchRecentCanvases(): Promise<{ items: readonly RecentCanvasItem[] }> {
  return callJson<{ items: readonly RecentCanvasItem[] }>("/me/recent-canvases");
}

export function deleteRecentCanvas(canvasId: string): Promise<void> {
  return callJson<void>(`/me/recent-canvases/${encodeURIComponent(canvasId)}`, {
    method: "DELETE",
  });
}

// ---- objects ----

export function createCanvasObject(
  canvasId: string,
  object: CanvasObject
): Promise<{ object: CanvasObject }> {
  return callJson<{ object: CanvasObject }>(
    `/canvases/${encodeURIComponent(canvasId)}/objects`,
    { method: "POST", body: JSON.stringify({ object }) }
  );
}

export interface PatchObjectInput {
  transform?: {
    x?: number | undefined;
    y?: number | undefined;
    w?: number | undefined;
    h?: number | undefined;
    rotation?: number | undefined;
    zOrder?: number | undefined;
  } | undefined;
  text?: string | undefined;
}

export function patchCanvasObject(
  canvasId: string,
  objectId: string,
  input: PatchObjectInput
): Promise<{ object: CanvasObject }> {
  return callJson<{ object: CanvasObject }>(
    `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
    { method: "PATCH", body: JSON.stringify(input) }
  );
}

export function deleteCanvasObject(canvasId: string, objectId: string): Promise<void> {
  return callJson<void>(
    `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
    { method: "DELETE" }
  );
}

// ---- uploads ----

export function presignImageUpload(params: {
  canvasId: string;
  objectId: string;
  contentType: string;
  contentLength: number;
}): Promise<PresignedUpload> {
  return callJson<PresignedUpload>(
    `/canvases/${encodeURIComponent(params.canvasId)}/uploads:image`,
    {
      method: "POST",
      body: JSON.stringify({
        objectId: params.objectId,
        contentType: params.contentType,
        contentLength: params.contentLength,
      }),
    }
  );
}

export function presignVideoUpload(params: {
  canvasId: string;
  objectId: string;
  contentType: string;
  contentLength: number;
}): Promise<PresignedUpload> {
  return callJson<PresignedUpload>(
    `/canvases/${encodeURIComponent(params.canvasId)}/uploads:video`,
    {
      method: "POST",
      body: JSON.stringify({
        objectId: params.objectId,
        contentType: params.contentType,
        contentLength: params.contentLength,
      }),
    }
  );
}

export interface UploadCompleteResult {
  ok: boolean;
  /** CloudFront 経由の公開 URL。未設定（ATTACHMENTS_PUBLIC_BASE_URL 未指定）の場合は null。 */
  publicUrl: string | null;
}

export function notifyUploadComplete(
  canvasId: string,
  bucket: string,
  key: string
): Promise<UploadCompleteResult> {
  return callJson<UploadCompleteResult>(
    `/canvases/${encodeURIComponent(canvasId)}/uploads:complete`,
    {
      method: "POST",
      body: JSON.stringify({ bucket, key }),
    }
  );
}

// ---- OGP ----

export function fetchOgp(url: string): Promise<OgFetchResult> {
  return callJson<OgFetchResult>(`/og?url=${encodeURIComponent(url)}`);
}

// ---- realtime ----

export function issueRealtimeSession(canvasId: string): Promise<RealtimeSessionTicket> {
  return callJson<RealtimeSessionTicket>(
    `/canvases/${encodeURIComponent(canvasId)}/realtime-session`
  );
}
